import { MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  APP_THEME_STORAGE_KEY,
  AppTheme,
  applyTheme,
  getPreferredTheme,
} from "../../theme";
import useStores from "../../utils/useStores";
import { canModerateHistoricNews } from "../../apps/HistoricNews/moderation";

function AppHeader() {
  const rootStore = useStores();
  const { isLoggedIn, profile } = rootStore.profileStore;
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<AppTheme>(getPreferredTheme);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-color-scheme: dark)");

    const followSystemTheme = (event: MediaQueryListEvent) => {
      if (!window.localStorage.getItem(APP_THEME_STORAGE_KEY)) {
        setTheme(event.matches ? "dark" : "light");
      }
    };

    const followStoredTheme = (event: StorageEvent) => {
      if (event.key === APP_THEME_STORAGE_KEY) setTheme(getPreferredTheme());
    };

    preference.addEventListener("change", followSystemTheme);
    window.addEventListener("storage", followStoredTheme);

    return () => {
      preference.removeEventListener("change", followSystemTheme);
      window.removeEventListener("storage", followStoredTheme);
    };
  }, []);

  function handleLogout() {
    setMenuOpen(false);
    rootStore.profileStore.logout(() => {});
  }

  function toggleTheme() {
    const nextTheme = theme === "dark" ? "light" : "dark";
    window.localStorage.setItem(APP_THEME_STORAGE_KEY, nextTheme);
    setTheme(nextTheme);
  }

  return (
    <header className="sticky top-0 z-50 flex h-10 w-full items-center justify-between border-b border-stone-300 bg-stone-100 px-3 font-mono text-zinc-800 dark:border-zinc-800 dark:bg-zinc-950 dark:text-stone-200 sm:px-4">
      <Link
        to="/"
        className="mr-3 shrink-0 text-sm font-bold tracking-widest hover:text-orange-700 dark:hover:text-orange-400"
      >
        LRNER
      </Link>
      <div className="flex min-w-0 items-center space-x-3 overflow-x-auto whitespace-nowrap text-xs uppercase tracking-wider [scrollbar-width:none] [&>*]:shrink-0 sm:space-x-4">
        <Link
          to="/"
          className="text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400"
        >
          News
        </Link>
        <Link to="/vibes" className="text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400">Vibes</Link>
        <Link
          to="/historic_news"
          className="text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400"
        >
          Stories
        </Link>
        <Link
          to="/modquiz"
          className="text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400"
        >
          Quiz
        </Link>
        <Link
          to="/radio"
          className="text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400"
        >
          Radio
        </Link>
        <Link
          to="/classical"
          className="text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400"
        >
          Classical
        </Link>
        <a
          href="https://blog.lrner.io"
          className="hidden text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400 sm:inline"
        >
          Blog
        </a>

        <button
          type="button"
          onClick={toggleTheme}
          className="grid h-7 w-7 place-items-center rounded-sm text-stone-600 hover:bg-stone-200 hover:text-orange-700 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-orange-700 dark:text-stone-400 dark:hover:bg-zinc-800 dark:hover:text-orange-400 dark:focus-visible:outline-orange-400"
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          {theme === "dark" ? (
            <SunIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <MoonIcon className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        {isLoggedIn ? (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="focus:outline-none"
            >
              <img
                src={profile.gravatar_url}
                alt={profile.username}
                className="w-8 h-8 rounded-full border border-gray-300"
              />
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-44 border border-stone-300 bg-stone-100 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
                <div className="border-b border-stone-300 px-4 py-2 text-sm text-stone-600 dark:border-zinc-700 dark:text-stone-400">
                  Signed in as{" "}
                  <span className="font-semibold text-zinc-900 dark:text-stone-100">
                    {profile.username}
                  </span>
                </div>
                <a
                  href="/modquiz/profile"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-200 dark:text-stone-300 dark:hover:bg-zinc-800"
                >
                  Profile
                </a>
                {canModerateHistoricNews(profile.email) && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-200 dark:text-stone-300 dark:hover:bg-zinc-800"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-200 dark:text-stone-300 dark:hover:bg-zinc-800"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link
            to="/login"
            className="text-xs font-medium text-stone-600 hover:text-orange-700 dark:text-stone-400 dark:hover:text-orange-400"
          >
            Login
          </Link>
        )}
      </div>
    </header>
  );
}

export default observer(AppHeader);
